import React, { useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { UploadFile as UploadIcon, FileDownload as DownloadIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { Waypoint } from '../types';

export type PathCostsMap = Record<string, Record<string, number>>;

interface PathCostsTabProps {
  waypoints: Waypoint[];
  imageSrc: string | null;
  showNames?: boolean;
  pathCosts: PathCostsMap;
  setPathCosts: (updater: (prev: PathCostsMap) => PathCostsMap) => void;
}

const parsePercent = (pctString: string): number => {
  const n = Number(String(pctString).replace('%', '').trim());
  return Number.isFinite(n) ? n : 0;
};

const PathCostsTab: React.FC<PathCostsTabProps> = ({
  waypoints,
  imageSrc,
  showNames = false,
  pathCosts,
  setPathCosts,
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);

  const [draftStartId, setDraftStartId] = useState<number | null>(null);
  const [draftEndId, setDraftEndId] = useState<number | null>(null);

  const edges = useMemo(() => {
    const list: Array<{ from: number; to: number; cost: number }> = [];
    Object.entries(pathCosts || {}).forEach(([fromStr, toMap]) => {
      const from = Number(fromStr);
      Object.entries(toMap || {}).forEach(([toStr, cost]) => {
        const to = Number(toStr);
        list.push({ from, to, cost: Number(cost) });
      });
    });
    return list;
  }, [pathCosts]);

  const waypointById = useMemo(() => {
    const map = new Map<number, Waypoint>();
    waypoints.forEach(w => map.set(Number(w.id), w));
    return map;
  }, [waypoints]);

  const handleImportCosts = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const parsed = JSON.parse(reader.result as string);
        if (!parsed || typeof parsed !== 'object' || !parsed.costs || typeof parsed.costs !== 'object') {
          throw new Error('Invalid path costs JSON: expected { costs: { "from": { "to": number } } }');
        }
        setPathCosts(() => parsed.costs as PathCostsMap);
      } catch (err) {
        console.error('Failed to parse path costs JSON', err);
        alert('Failed to load path costs. Ensure it is an object with a "costs" field mapping from->to costs.');
      } finally {
        e.target.value = '';
      }
    };
    reader.readAsText(file);
  };

  const exportCostsJSON = () => {
    const data = { costs: pathCosts };
    const dataStr = JSON.stringify(data, null, 2);
    const blob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'path_costs.json';
    a.click();
    URL.revokeObjectURL(url);
  };

  const addEdge = (fromId: number, toId: number, cost: number) => {
    const fromKey = String(fromId);
    const toKey = String(toId);
    setPathCosts(prev => {
      const next = { ...prev };
      const toMap = { ...(next[fromKey] || {}) };
      toMap[toKey] = cost;
      next[fromKey] = toMap;
      return next;
    });
  };

  const removeEdge = (fromId: number, toId: number) => {
    const fromKey = String(fromId);
    const toKey = String(toId);
    setPathCosts(prev => {
      const next = { ...prev };
      if (!next[fromKey]) return next;
      const toMap = { ...next[fromKey] };
      delete toMap[toKey];
      if (Object.keys(toMap).length === 0) delete next[fromKey];
      else next[fromKey] = toMap;
      return next;
    });
  };

  const updateEdgeCost = (fromId: number, toId: number, cost: number) => {
    const fromKey = String(fromId);
    const toKey = String(toId);
    setPathCosts(prev => {
      const next = { ...prev };
      if (!next[fromKey]) next[fromKey] = {};
      next[fromKey][toKey] = cost;
      return next;
    });
  };

  const handleMarkerClick = (id: number) => {
    if (draftStartId === null) {
      setDraftStartId(id);
      setDraftEndId(null);
      return;
    }
    if (draftStartId !== null && draftEndId === null) {
      if (id === draftStartId) return; // ignore same id
      setDraftEndId(id);
      const input = prompt('Enter path cost (number)', '1');
      const cost = input ? Number(input) : NaN;
      if (!Number.isFinite(cost) || cost <= 0) {
        alert('Invalid cost value. Please enter a positive number.');
        setDraftStartId(null);
        setDraftEndId(null);
        return;
      }
      addEdge(draftStartId, id, cost);
      setDraftStartId(null);
      setDraftEndId(null);
    }
  };

  return (
    <Box>
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">Path Costs</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="outlined" startIcon={<UploadIcon />} component="label" color="secondary">
            Import JSON
            <input hidden type="file" accept="application/json" onChange={handleImportCosts} />
          </Button>
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={exportCostsJSON}
            disabled={!pathCosts || Object.keys(pathCosts).length === 0}
          >
            Export JSON
          </Button>
        </Box>
      </Box>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="body2" gutterBottom>
            Click two waypoints to create a connection. A prompt will ask for the path cost.
          </Typography>
          <Paper
            ref={containerRef}
            sx={{
              position: 'relative',
              overflow: 'hidden',
              border: '1px solid',
              borderColor: 'divider',
              minHeight: 300,
            }}
          >
            {imageSrc ? (
              <img
                ref={imgRef}
                src={imageSrc}
                alt="Map"
                style={{ width: '100%', height: 'auto', display: 'block', userSelect: 'none' }}
              />
            ) : (
              <Box sx={{ p: 6, textAlign: 'center', color: 'text.secondary' }}>
                <Typography variant="body1">Upload a map image on the Waypoints tab to begin.</Typography>
              </Box>
            )}

            {/* Lines overlay */}
            {imageSrc && (
              <Box sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none' }}>
                <svg width="100%" height="100%" viewBox="0 0 100 100" preserveAspectRatio="none">
                  {edges.map((e, idx) => {
                    const from = waypointById.get(Number(e.from));
                    const to = waypointById.get(Number(e.to));
                    if (!from || !to) return null;
                    const x1 = parsePercent(from.left);
                    const y1 = parsePercent(from.top);
                    const x2 = parsePercent(to.left);
                    const y2 = parsePercent(to.top);
                    const isDraft = draftStartId === e.from && draftEndId === e.to;
                    return (
                      <line
                        key={`${e.from}-${e.to}-${idx}`}
                        x1={x1}
                        y1={y1}
                        x2={x2}
                        y2={y2}
                        stroke={isDraft ? '#ff9800' : '#1976d2'}
                        strokeWidth={isDraft ? 0.6 : 0.4}
                        opacity={0.85}
                      />
                    );
                  })}
                </svg>
              </Box>
            )}

            {/* Markers */}
            {imageSrc &&
              waypoints.map(w => (
                <Box
                  key={w.id}
                  sx={{
                    position: 'absolute',
                    top: w.top,
                    left: w.left,
                    transform: 'translate(-50%, -50%)',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 0.5,
                    pointerEvents: 'none',
                  }}
                >
                  {showNames && (
                    <Box
                      sx={{
                        px: 0.5,
                        py: 0.25,
                        borderRadius: 1,
                        backgroundColor: 'rgba(0,0,0,0.7)',
                        color: 'white',
                        fontSize: 11,
                        lineHeight: 1.2,
                        pointerEvents: 'none',
                      }}
                    >
                      {w.title || `Waypoint ${w.id}`}
                    </Box>
                  )}
                  <Box
                    onClick={() => handleMarkerClick(Number(w.id))}
                    sx={{
                      width: 16,
                      height: 16,
                      borderRadius: '50%',
                      backgroundColor: draftStartId === w.id ? 'warning.main' : 'success.main',
                      boxShadow: draftStartId === w.id ? 2 : 1,
                      cursor: 'pointer',
                      border: '2px solid white',
                      pointerEvents: 'auto',
                    }}
                  />
                </Box>
              ))}
          </Paper>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Connections
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>From</TableCell>
                <TableCell>To</TableCell>
                <TableCell>Cost</TableCell>
                <TableCell align="center" width={90}>
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {edges.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4}>
                    <Typography variant="body2" color="text.secondary">
                      No connections yet. Click two markers on the map to add one.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                edges.map(edge => (
                  <TableRow key={`${edge.from}-${edge.to}`}>
                    <TableCell>{edge.from}</TableCell>
                    <TableCell>{edge.to}</TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        type="number"
                        value={edge.cost}
                        onChange={e => {
                          const v = Number(e.target.value);
                          if (Number.isFinite(v) && v > 0) updateEdgeCost(edge.from, edge.to, v);
                        }}
                        inputProps={{ min: 0, step: 0.1 }}
                      />
                    </TableCell>
                    <TableCell align="center" width={90}>
                      <Tooltip title="Remove connection">
                        <IconButton color="error" onClick={() => removeEdge(edge.from, edge.to)}>
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </Box>
  );
};

export default PathCostsTab;
